/**
 * The ENGLISH catalogue for copy that core itself produces — the base, and the source of
 * `CoreCopyKey`.
 *
 * ## Why core has a catalogue of its own at all
 *
 * `packages/ui/kiosk/copy` is the catalogue for words a SCREEN draws, and its rule — "a screen
 * holds no literal" — was always satisfied: no kiosk screen has ever had English in it. The
 * copy below arrives at those screens as PROPS, from core, which is underneath the boundary
 * that rule guards. A payment tile reading "Pay with UPI", the phone prompt, every offer row
 * and most error messages came from here, and none of them could be translated.
 *
 * Core cannot use the kiosk's catalogue: `packages/core` must not import `@rista/ui` (audit
 * check E), and that catalogue lives in `packages/ui`. So core owns this one. The two are
 * disjoint key sets resolved by different functions — `useKioskCopy()` there, `copy()` here —
 * and neither needs to know about the other.
 *
 * ## Adding a language
 *
 * One file beside this, plus a line in `CATALOGUES` in `./index`. `Catalogue<CoreCopyKey>` is a
 * TOTAL Record, so a missing key is a compile error naming it. ⚠️ A language needs a file HERE
 * **and** one in `packages/ui/kiosk/copy` — the kiosk's words are split across the two by which
 * layer produces them, and a translator handed only one of them ships a half-translated screen.
 *
 * ## Values
 *
 * Identical contract to the kiosk catalogue: a plain string, or `{ one, other }` where a count
 * changes the wording, and `${name}` interpolated from the params. `resolveCopy` does both.
 *
 * `satisfies`, not an annotation — an annotation widens the keys to `string` and `CoreCopyKey`
 * stops catching typos, which is most of the point.
 */
import type { CopyValue } from "../utils/i18n";

export const en = {
  /* ── PAYMENT — the kiosk's tiles and its in-progress lines ─────────────────────────── */
  // What the customer picks between. `buildKioskPaymentMethods` labels each tile with one.
  "payment.tile.counter": "Pay at counter",
  "payment.tile.upi": "Pay with UPI",
  "payment.tile.card": "Credit / Debit card",
  // The status line under the amount, one per leg state — see `kioskLegMessage`.
  "payment.leg.scanQr": "Please scan the QR code to make payment",
  "payment.leg.useCardMachine": "Complete your transaction on the payment machine",
  "payment.leg.posted": "Payment received. Placing your order…",
  "payment.leg.failed": "That payment did not go through.",
  "payment.leg.cancelled": "Payment cancelled.",
  // The kiosk's own error surface — no toasts, so these are read on the payment screen.
  "payment.noTransport": "This kiosk can't take payment right now. Please order at the counter.",
  "payment.orderFailed": "We couldn't place that order. Please ask a member of staff.",
  // A payment the provider declined or that errored — never one the customer cancelled.
  // See `didLegJustFail`.
  "payment.failed": "Payment failed. Please try again or choose another way to pay.",

  /* ── CONTACT — the phone-number and OTP steps ──────────────────────────────────────── */
  "contact.phone.title": "Enter your mobile number",
  "contact.phone.subtitleLoyalty": "Access loyalty benefits and get your digital bill.",
  "contact.phone.subtitleBillOnly": "Get your digital bill on this number.",
  "contact.phone.verify": "Verify number",
  "contact.phone.proceed": "Proceed",
  "contact.otp.title": "Enter verification code",
  "contact.otp.subtitle": "A ${length}-digit code was sent to ${phone}. Enter it below.",
  "contact.otp.verify": "Verify code",
  "contact.otp.resend": "Resend code",
  "contact.invalidPhone": "Enter a valid 10-digit mobile number",
  "contact.sendFailed": "We couldn't send the code. Please try again.",
  "contact.verifyFailed": "That code doesn't match. Please try again.",
  "contact.resendFailed": "We couldn't resend the code. Please try again.",

  /* ── LOYALTY — the points card, its tooltip, and the redemption failures ───────────── */
  // ⚠️ `${label}` is the merchant's own collectible noun ("Points", "Beans"), already
  // resolved by `collectibleLabel()`. It is DATA, not copy — never translated here.
  "loyalty.pointsAvailable": "${label} available: ${total}",
  "loyalty.card.using": "Using ${applied} of ${total} ${label} (${amount} off)",
  "loyalty.card.use": "Use ${max} of ${total} ${label}",
  "loyalty.blocked.minOrder": "Add more items — orders above ${amount} can use ${label}",
  "loyalty.blocked.someItems": "${base}. Some items are not eligible",
  "loyalty.blocked.excludedItems": "${label} cannot be used on the items in this order",
  "loyalty.blocked.thisOrder": "${label} cannot be used on this order",
  "loyalty.tooltip.burn": "You can redeem up to ${percent}% of ${method}",
  "loyalty.tooltip.mov": "${base} (${mov})",
  "loyalty.tooltip.max": "${base}. Maximum ${amount} can be redeemed per transaction.",
  "loyalty.redemptionFailed": "Couldn't redeem points. Please try again.",
  "loyalty.releaseFailed": "Couldn't remove points. Your total hasn't changed.",
  "loyalty.verificationFailed": "Couldn't confirm that code. Please try again.",

  /* ── DISCOUNT — offer rows and the failures the sheet shows ────────────────────────── */
  "discount.offerFallback": "Offer available",
  "discount.limitExceeded": "Deal or Discount Limit Exceed",
  "discount.notApplicable": "Discount not applicable",
  "discount.itemsMissing": "Items for this discount are not available in the cart",
  "discount.offerFailed": "Couldn't apply that offer. Please try again.",
  "discount.otpFailed": "Couldn't confirm that code. Please try again.",
  "discount.phoneMissing": "Customer phone number is missing",
  "discount.couponAlreadyApplied": "Coupon already applied.",
  "discount.rewardItemUnavailable": "This reward's item could not be added to the cart",
  "discount.offerItemUnavailable": "This item could not be added to the cart",
  // the discount session's messages (`discounts/session/messages.ts`): web v1 `DiscountDeals`' words where it had them
  "discount.verified": "Validate successfully",
  "discount.alreadyApplied": "This discount is already applied.",
  "discount.inactive": "This discount is not active.",
  "discount.chosenItemOnly": "This discount applies to a chosen item only.",
  // a loyalty reward the merchant paused or whose end date has passed (web v1's row banners, `ItemLoyaltyRewardSection`)
  "discount.rewardPaused": "Deal unavailable - reward paused until merchant relinks it",
  "discount.rewardExpired": "This reward has expired.",
  "discount.belowMinimum": "This discount needs a bill of at least ${amount}.",
  "discount.needsCustomer": "Add a customer to use this discount.",
  "discount.notPermitted": "You are not allowed to change discounts.",
  "discount.notApplicableOnBill": "Discount not applicable. Please check requirements (e.g., select customer).",
  "discount.wrongOtp": "Please enter correct otp",
  "discount.offline": "You are offline. Connect to the internet and try again.",
  "discount.approval.closedOrder": "Cant override discount in closed order",
  "discount.key.title": "Discounts & Deals",
  "discount.key.checkout": "Checkout",
  "discount.key.save": "Save to Cart",
  "discount.key.edit": "Edit the focused deal",
  "discount.approval.needed":
    "Applied discount of ${amount} is higher than the permitted limit ${percent}%\n  Please click on request for approval to move forward",

  /* ── SHORTCUTS — a refused key's fallback, when the shortcut gives no `denial` ───────── */
  // v1's one message for it (`components/Cart/index.tsx:405`), SHORTCUTS.md §5.5.
  "shortcuts.refused": "This keyboard shortcut is restricted",

  /* ── ALERT — v2's warning dialog (`@rista/ui/library/components/AlertDialog`), v1's words ─────── */
  "alert.warning": "Warning",
  "alert.ok": "Ok",
  "alert.close": "Close",

  /* ── PRINTING — what the POS operator sees when something does not print ────────────── */
  // v1's two messages: `data/constants` DEFAULT_PRINTER_MSG and posErrors' UI_ERR_SELECT_PRINTER_FROM_POS.
  "printing.noDefaultPrinter": "A default printer must be selected.",
  "printing.configurePrinter": "Please configure a printer for use from POS to use this feature.",

  /* ── COMMIT — why a save, hold or print stopped before posting (`ordering/commit/guards`) ── */
  // v1's words (`utils/posErrors`, web's constants) and the old FE's, where web had no check.
  "commit.emptyBasket": "No item(s) in the order to process.",
  "commit.quickCilverOverBill": "To continue, you have to cancel the QuickCilver payment.",
  "commit.discountNeedsCustomer":
    "Discount which need customer verification is applied. Please add customer details or remove the discount to proceed",
  "commit.customerOtpRequired": "This discount needs the customer's verification. Verify the customer to proceed.",
  "commit.noChannelDefinition": "Channel definition not found",
  "commit.onlineSaleOffline": "Online orders cannot be operated upon in offline mode. Please go online.",
  "commit.labelRequired": "Label is required for this channel.",
  "commit.deliveryOrPickupRequired": "Delivery or Pickup info is required for this channel.",
  "commit.pickupRequired": "Pickup info is required for this channel.",
  "commit.deliveryRequired": "Delivery info is required for this channel.",
  "commit.sourceOrderNumberRequired": "Source Order Number is required.",
  "commit.customerRequired": "This sale requires customer authentication. Please attach a customer to proceed.",
  "commit.customerNotAuthenticated": "Customer not authenticated",
  "commit.negativeBill":
    "The sale is invalid as the total sale value is less than 0. Please remove discounts or add items.",

  /* ── CART — the common cart's actions and bill (`orderTaking/cartPanel`, plan §3.5) ──────── */
  // web v1's words: `components/Cart` (its bar, header and bill), `cartHeader.tsx`, `RequestApprovalCta`.
  "cart.action.discount": "Discount",
  "cart.action.requestApproval": "Request For Approval",
  "cart.action.save": "Save Order",
  "cart.action.hold": "Hold order",
  "cart.action.viewBill": "View/Print Bill",
  "cart.action.quickSettle": "Quick Settle",
  "cart.action.checkout": "Checkout",
  "cart.action.closeOrder": "Close order",
  "cart.action.deleteOrder": "Delete order",
  "cart.action.charges": "Charges",
  "cart.action.orderNote": "Order Notes",
  "cart.action.saleBy": "Sale By",
  // the old FE's Support basket: "Order" places it Open, "Pay" takes payment first (plan §9.3)
  "cart.action.placeOrder": "Order",
  "cart.action.payAndPlace": "Pay",
  "cart.bill.subTotal": "Sub Total",
  "cart.bill.discounts": "Discounts",
  "cart.bill.couponDiscount": "Coupon discount",
  "cart.bill.otherCharges": "Other Charges",
  "cart.bill.directCharges": "Direct charges",
  "cart.bill.taxes": "Taxes",
  "cart.bill.taxInPrices": "Tax included in item prices",
  "cart.bill.amount": "Bill Amount",
  "cart.bill.rounded": "Bill Amount (rounded)",
  /* My Orders' bill (v1 `MyOrderCart`): its charges row and the balance still due */
  "cart.bill.charges": "Charges",
  "cart.bill.balance": "Balance",
  "cart.bill.itemCount": "Total number of items",

  /* ── CHANNEL SWITCH — its keys, for the live help (`orderTaking/channelSwitch`) ──────────── */
  // web v1's chords: `components/ChannelSwitch` (the header row) and `ChannelSwitchPopup` (the list).
  "channelSwitch.key.allChannels": "All channels",
  "channelSwitch.key.pinned": "Pinned channels",
  "channelSwitch.key.previous": "Previous channel",
  "channelSwitch.key.next": "Next channel",
  "channelSwitch.key.choose": "Order on this channel",
  "channelSwitch.key.close": "Close",
  "channelSwitch.key.pinnedGroup": "Go to pinned channels",
  "channelSwitch.key.moreGroup": "Go to more channels",
  "channelSwitch.key.pin": "Pin channel",
  "channelSwitch.key.unpin": "Unpin channel",
  "channelSwitch.key.save": "Save",

  /* ── BROWSE — New Order's keys over the menus, the rail and the items (`orderTaking/newOrder/browse`) ── */
  // web v1's chords: `Containers/Categories`, `commonComponents/{SubCategories,MenuList,ItemsView}`, `searchBar`.
  "browse.recommended": "Recommended",
  "browse.mostSold": "Most sold Items",
  "browse.chefRecommended": "Chef Recommended",
  "browse.key.previousCategory": "Previous category",
  "browse.key.nextCategory": "Next category",
  "browse.key.previousSubCategory": "Previous sub-category",
  "browse.key.nextSubCategory": "Next sub-category",
  "browse.key.menu": "Menu ${n}",
  "browse.key.viewType": "Change the view",
  "browse.key.itemUp": "Item above",
  "browse.key.itemDown": "Item below",
  "browse.key.itemLeft": "Previous item",
  "browse.key.itemRight": "Next item",
  "browse.key.pressItem": "Add the item",
  "browse.key.leaveItems": "Leave the items",
  /* item search: web v1's words (`Containers/BarCodeSearch`, `utils/pos_errors`) */
  "itemSearch.key.open": "Search items",
  "itemSearch.key.close": "Back to the menu",
  "itemSearch.key.next": "Next result",
  "itemSearch.key.previous": "Previous result",
  "itemSearch.key.press": "Add the result",
  "itemSearch.expired": "Item has expired.",
  "itemSearch.invalidQuantity": "Invalid quantity.",
  "itemSearch.fractionNotAllowed": "Fractional quantity is not allowed for this item",

  /* ── CUSTOMISER — New Order's item popup keys and outcomes (`orderTaking/newOrder/customiser`) ── */
  // web v1's popup keys (`commonComponents/CustomiseItemPopup`) and its search add toast (`BarCodeSearch`).
  "customiser.key.previousAttribute": "Previous variant",
  "customiser.key.nextAttribute": "Next variant",
  "customiser.key.previousValue": "Previous value",
  "customiser.key.nextValue": "Next value",
  "customiser.key.previousOption": "Previous add-on",
  "customiser.key.nextOption": "Next add-on",
  "customiser.key.addOption": "Add the add-on",
  "customiser.key.removeOption": "Remove the add-on",
  "customiser.key.quantity": "Edit the quantity",
  "customiser.key.add": "Add to cart",
  "customiser.key.close": "Close",
  "customiser.added": "${name} is added to the cart.",
  "customiser.unavailable": "This item cannot be added now.",

  /* ── RESERVATIONS — the partners a booking comes from (RESERVATIONS_V2_PLAN.md §4.5) ──── */
  "reservations.partner.pos": "POS",
  "reservations.partner.dotpe": "Rista Booking",
  "reservations.partner.zomato": "Zomato",
  "reservations.partner.swiggy": "Swiggy",
  "reservations.partner.eazydiner": "EazyDiner",
  "reservations.partner.other": "Partner",

  // Why a reservations gateway call did not do what was asked (RESERVATIONS_V2_PLAN.md §4.6, decision 12: errors are said,
  // with Retry). The wording follows the old FE's backend_errors.js (:2, :83, :89, :108, :193, :472).
  "reservations.refusal.changedElsewhere": "This booking was changed on another terminal. It has been reloaded; please try again.",
  "reservations.refusal.notFound": "This booking no longer exists.",
  "reservations.refusal.invalidInput": "The server did not accept these details. Please check them and try again.",
  "reservations.refusal.notPermitted": "Your login does not have access to reservations.",
  "reservations.refusal.offline": "You are offline. Reservations need an internet connection.",
  "reservations.refusal.network": "Could not reach the server. Please try again.",
  "reservations.refusal.server": "The server had a problem. Please try again.",
  "reservations.refusal.sessionEnded": "Your session has ended. Please sign in again.",
  "reservations.refusal.tooManyEmails": "Too many emails were sent. Please try again in a minute.",
  "reservations.refusal.invalidBrand": "Choose a brand to send the WhatsApp message from.",
  "reservations.refusal.cannotSave": "This booking cannot be saved as it is. Please reload it and try again.",
  "reservations.refusal.notAllowedNow": "This can no longer be done for this booking.",
  "reservations.refusal.failed": "Something went wrong. Please try again.",

  // RV2d: the presses on a booking (RESERVATIONS_V2_PLAN.md §8.2): what a landed press says (decision 52), why a press
  // is not offered (`rules/actions.ts`' reasons) and what a reason or table sheet asks for. Keys: `reservations.press.*`,
  // `reservations.blocked.*`, `reservations.input.*`. RV2d WORDS END
  "reservations.press.confirmed": "Reservation confirmed",
  "reservations.press.rejected": "Reservation rejected",
  "reservations.press.cancelled": "Reservation cancelled",
  "reservations.press.tableAssigned": "${table} assigned to ${name}",
  "reservations.press.tableReleased": "${table} released",
  "reservations.blocked.offline": "You are offline",
  "reservations.blocked.notPermitted": "Your login cannot do this",
  "reservations.blocked.cancelled": "The reservation is cancelled",
  "reservations.blocked.completed": "The visit is complete",
  "reservations.blocked.timeUnknown": "The reservation's time cannot be read",
  "reservations.blocked.over": "The reservation time is over",
  "reservations.blocked.notToday": "Only today's reservations can take a table",
  "reservations.blocked.orderOpen": "The guests already have an open order",
  "reservations.blocked.notPending": "Only a pending reservation can do this",
  "reservations.blocked.notConfirmed": "Only a confirmed reservation can do this",
  "reservations.blocked.confirmFirst": "Confirm the reservation first",
  "reservations.blocked.notNow": "The reservation is not running now",
  "reservations.blocked.notLater": "The reservation has already started",
  "reservations.blocked.partnerDoesNotAllow": "The booking partner does not allow this here",
  "reservations.blocked.noTable": "No table is assigned",
  "reservations.blocked.seated": "The guests are already seated",
  "reservations.input.reasonRequired": "Enter a reason",
  "reservations.input.reasonTooLong": "The reason is too long",
  "reservations.input.reasonTagLike": "The reason cannot contain tags",
  "reservations.input.tableRequired": "Choose a table",

  // RV2e forms: Create and Edit (decisions 43–46, 57–60): what a landed form says and each field's problem.
  // Keys: `reservations.form.*`, `reservations.problem.*`. RV2e FORM WORDS END
  "reservations.form.created": "Reservation created for ${name} at ${time}",
  "reservations.form.updated": "Reservation updated",
  "reservations.problem.invalid": "Check this field",
  "reservations.problem.firstName.required": "Enter the guest's first name",
  "reservations.problem.firstName.tooLong": "The first name is too long",
  "reservations.problem.firstName.tagLike": "The first name cannot contain tags",
  "reservations.problem.lastName.tooLong": "The last name is too long",
  "reservations.problem.lastName.tagLike": "The last name cannot contain tags",
  "reservations.problem.phoneNumber.required": "Enter the guest's phone number",
  "reservations.problem.phoneNumber.invalidPhone": "Enter a valid phone number for this country",
  "reservations.problem.phoneCountryCode.required": "Choose the phone number's country",
  "reservations.problem.partySize.notWhole": "Party size must be a whole number",
  "reservations.problem.partySize.range": "Party size is ${min} to ${max}",
  "reservations.problem.date.required": "Choose a date",
  "reservations.problem.date.pastDate": "Choose today or a later date",
  "reservations.problem.slotTime.required": "Choose a time slot",
  "reservations.problem.slotTime.slotNotOffered": "That time slot is not offered. Choose another",
  "reservations.problem.durationMinutes.notWhole": "Duration must be a whole number of minutes",
  "reservations.problem.durationMinutes.range": "Duration is ${min} to ${max} minutes",
  "reservations.problem.durationMinutes.notStep": "Duration goes in steps of ${step} minutes",
  "reservations.problem.reference.tooLong": "The reference can have up to ${max} characters",
  "reservations.problem.reference.tagLike": "The reference cannot contain tags",
  "reservations.problem.notes.tooLong": "Notes can have up to ${max} characters",
  "reservations.problem.notes.tagLike": "Notes cannot contain tags",
  "reservations.problem.sourceLabel.labelReserved": "Choose another source: this name is kept for partner bookings",
  "reservations.problem.sourceLabel.tooLong": "The source is too long",
  "reservations.problem.sourceLabel.tagLike": "The source cannot contain tags",

  // RV2e send: Send appointment (§4.7): what a landed send says and each field's problem. Keys:
  // `reservations.send.*`. RV2e SEND WORDS END
  // The landed sentence is the designs' (decision 76), shorter than v1's `SENT_APPOINTMENT`: it names the way, not the contact.
  "reservations.send.landed": "Appointment sent via ${channel}",
  "reservations.send.channel.whatsapp": "WhatsApp",
  "reservations.send.channel.sms": "SMS",
  "reservations.send.channel.email": "Email",
  "reservations.send.problem.number": "Enter a valid 10-digit mobile number",
  "reservations.send.problem.email": "Enter a valid email address",
  "reservations.send.problem.brand": "Choose a brand to send from",
  "reservations.send.problem.noBrand": "No brand is set up for WhatsApp at this branch",

  // RV2f: the seat both ways in (decisions 11, 25, 41, 42, 64, 65): why a booking cannot be seated, and what is said
  // when walk-ins take a reserved table and the booking could not let it go. Keys: `reservations.seat.*`. RV2f SEAT WORDS END
  "reservations.seat.cancelled": "The reservation is cancelled, so it cannot be seated",
  "reservations.seat.completed": "The visit is complete, so the reservation cannot be seated",
  "reservations.seat.confirmFirst": "Confirm the reservation before seating it",
  "reservations.seat.notConfirmed": "Only a confirmed reservation can be seated",
  "reservations.seat.timeUnknown": "The reservation's time cannot be read, so it cannot be seated",
  "reservations.seat.notNow": "The reservation can be seated only while it is running",
  "reservations.seat.orderOpen": "The guests already have an open order",
  "reservations.seat.tableOccupied": "Table ${table} already has an open order",
  "reservations.seat.walkInReleaseFailed": "${guest}'s booking could not be updated and still holds ${table}",
  // RV4a: said after ordering opened: for the booked guests (`seated`), and for walk-ins on a table (`walkInStarted`; decision 76).
  "reservations.seat.seated": "${name} seated on ${table}",
  "reservations.seat.walkInStarted": "Order started on ${table}",

  // RV4a: the views' core words (RESERVATIONS_V2_PLAN.md §8.4): a hand-pressed Refresh (decision 76) and the screen's keys
  // in the shortcut help (decision 75). Keys: `reservations.day.*`, `reservations.key.*`. RV4a WORDS END
  "reservations.day.refreshed": "Reservations refreshed",
  "reservations.key.title": "Reservations",
  "reservations.key.previousDay": "Previous day",
  "reservations.key.nextDay": "Next day",
  "reservations.key.today": "Today",
  "reservations.key.newReservation": "New reservation",
  "reservations.key.search": "Search reservations",
  "reservations.key.closeBooking": "Close the reservation",
  "reservations.key.closeSheet": "Close",
  // RV5f-3 round 2 (decision 133): the seat sheet's Ctrl+Enter, named as its confirm button is.
  "reservations.key.seatConfirm": "Seat & Start Order",

  /* ── CAPABILITIES — why an action is not available (CAPABILITIES.md §4.3) ──────────────── */
  // v1's one message, `ACCESS_DISABLED_MESSAGE` (helpers/featureRestrictionHelper), kept for a permission.
  "capabilities.permission": "Current user does not have permission to take this action",
  "capabilities.module": "Not included in your licence",
  "capabilities.licence": "Not included in your licence",
  "capabilities.config": "Turned off in your settings",
  "capabilities.surface": "Not available on this screen",
  "capabilities.platform": "Not available on this device",
  "capabilities.offline": "Not available offline",
  "capabilities.kiosk": "Not available in kiosk mode",
  "capabilities.unknown": "Not available",
  "capabilities.role": "Not allowed for your role",
  "capabilities.session": "Not available yet",
  "capabilities.suspended": "Not available right now",
} satisfies Record<string, CopyValue>;
